import { AlertCircle, Calendar, CheckCircle, ChevronLeft, ChevronRight, Circle, Plus, Search } from 'lucide-react'
import type React from 'react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useRaceResults } from '../../hooks/useRaceResults'
import { theme } from '../../theme/theme'
import {
  formatTime,
  getRaceTypeConfig,
  getRaceTypesByCategory,
  type RaceResult,
  type RaceResultsFilter,
} from '../../types/raceResults'
import { Button } from '../common/Button'
import { RaceResultModal } from '../modals/RaceResultModal'

const Container = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.sm} 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  transition: ${({ theme }) => theme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`

const FilterDropdown = styled.select`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  min-width: 140px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableContainer = styled.div`
  overflow-x: auto;
`

const TableRow = styled.tr`
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`

const TableHeader = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  vertical-align: middle;
`

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${({ $status, theme }) => {
    switch ($status) {
      case 'completed':
        return `
          background: ${theme.colors.secondaryLight};
          color: ${theme.colors.textInverted};
        `
      case 'upcoming':
        return `
          background: ${theme.colors.secondaryLight};
          color: ${theme.colors.text};
        `
      case 'dnf':
      case 'dns':
        return `
          background: ${theme.colors.secondaryLight};
          color: ${theme.colors.text};
        `
      default:
        return `
          background: ${theme.colors.secondaryLight};
          color: ${theme.colors.text};
        `
    }
  }}
`

const RaceType = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.textSecondary}10;
  color: ${({ theme }) => theme.colors.textSecondary};
`

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
`

const ActionButton = styled.button<{ $variant?: 'edit' | 'delete' | 'view' }>`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};
  
  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'edit':
        return `
          color: ${theme.colors.primary};
          &:hover {
            background: ${theme.colors.surfaceHover};
            border-color: ${theme.colors.primary};
          }
        `
      case 'delete':
        return `
          color: ${theme.colors.error};
          &:hover {
            background: ${theme.colors.error};
            border-color: ${theme.colors.error};
          }
        `
      case 'view':
      default:
        return `
          color: ${theme.colors.textSecondary};
          &:hover {
            background: ${theme.colors.surfaceHover};
            color: ${theme.colors.text};
          }
        `
    }
  }}
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const PaginationInfo = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const PageSelector = styled.select`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.sm};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

const RankCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const RankValue = styled.span<{ $type: 'overall' | 'ageGroup' }>`
  font-size: ${({ $type }) => '12px'};
  font-weight: ${({ $type }) => '400'};
  color: ${({ theme, $type }) => theme.colors.textSecondary};
`

interface RaceResultsTableProps {
  athleteId: string
}

export const RaceResultsTable: React.FC<RaceResultsTableProps> = ({ athleteId }) => {
  // Use the race results hook directly
  const {
    raceResults: data,
    loading,
    error,
    addRaceResult,
    updateRaceResult,
    deleteRaceResult,
  } = useRaceResults(athleteId)

  const [filters, setFilters] = useState<RaceResultsFilter>({})
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showRaceResultModal, setShowRaceResultModal] = useState(false)
  const [editingRaceResult, setEditingRaceResult] = useState<RaceResult | undefined>(undefined)

  const triathlonRaces = getRaceTypesByCategory('triathlon')
  const runningRaces = getRaceTypesByCategory('running')
  const cyclingRaces = getRaceTypesByCategory('cycling')
  const swimmingRaces = getRaceTypesByCategory('swimming')
  const otherRaces = getRaceTypesByCategory('other')

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'completed', label: 'Finished' },
    { value: 'dnf', label: 'Did Not Finish (DNF)' },
    { value: 'dns', label: 'Did Not Start (DNS)' },
  ]

  const currentYear = new Date().getFullYear()
  const yearOptions = [
    { value: '', label: 'All Years' },
    ...Array.from({ length: 10 }, (_, i) => {
      const year = currentYear - i
      return { value: year.toString(), label: year.toString() }
    }),
  ]

  const filteredData = useMemo(() => {
    let filtered = [...data]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(
        (race) => race.name.toLowerCase().includes(searchLower) || race.location?.toLowerCase().includes(searchLower)
      )
    }

    if (filters.raceType) {
      filtered = filtered.filter((race) => race.raceType === filters.raceType)
    }

    if (filters.status) {
      filtered = filtered.filter((race) => race.status === filters.status)
    }

    if (filters.year) {
      filtered = filtered.filter((race) => new Date(race.date).getFullYear().toString() === filters.year)
    }

    // Sort by date (most recent first)
    filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

    return filtered
  }, [data, filters])

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, currentPage, pageSize])

  const totalPages = Math.ceil(filteredData.length / pageSize)

  const handleFilterChange = (key: keyof RaceResultsFilter, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }))
    setCurrentPage(1)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  const handleCloseModal = () => {
    setShowRaceResultModal(false)
    setEditingRaceResult(undefined)
  }

  const onAdd = () => {
    setEditingRaceResult(undefined)
    setShowRaceResultModal(true)
  }

  const onEdit = (raceResult: RaceResult) => {
    setEditingRaceResult(raceResult)
    setShowRaceResultModal(true)
  }

  const onView = (raceResult: RaceResult) => {
    onEdit(raceResult) // For now, view is the same as edit
  }

  const onDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this race result?')) {
      await deleteRaceResult(id)
    }
  }

  const handleSaveRaceResult = async (raceResultData: Omit<RaceResult, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingRaceResult) {
      await updateRaceResult(editingRaceResult.id, raceResultData)
    } else {
      await addRaceResult(raceResultData)
    }
  }

  const handleDeleteRaceResult = async (id: string) => {
    await deleteRaceResult(id)
  }

  if (loading) {
    return (
      <Container>
        <EmptyState>Loading race results...</EmptyState>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Header></Header>
        <EmptyState style={{ color: 'red' }}>Error loading race results: {error}</EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <SearchBox>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search races..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </SearchBox>

        <FilterDropdown value={filters.raceType || ''} onChange={(e) => handleFilterChange('raceType', e.target.value)}>
          <option value="">All Race Types</option>
          <optgroup label="Triathlon">
            {triathlonRaces.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Running">
            {runningRaces.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Cycling">
            {cyclingRaces.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Swimming">
            {swimmingRaces.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </optgroup>
          {otherRaces.length > 0 && (
            <optgroup label="Other">
              {otherRaces.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </optgroup>
          )}
        </FilterDropdown>

        <FilterDropdown value={filters.status || ''} onChange={(e) => handleFilterChange('status', e.target.value)}>
          {statusOptions.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </FilterDropdown>

        <FilterDropdown value={filters.year || ''} onChange={(e) => handleFilterChange('year', e.target.value)}>
          {yearOptions.map((year) => (
            <option key={year.value} value={year.value}>
              {year.label}
            </option>
          ))}
        </FilterDropdown>

        <Button variant="primary" onClick={onAdd}>
          <Plus size={16} />
          Add Race
        </Button>
      </Header>

      <TableContainer>
        {paginatedData.length === 0 ? (
          <EmptyState>
            {filteredData.length === 0 && data.length > 0
              ? 'No race results match your filters.'
              : 'No race results found.'}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <TableHeader>Date</TableHeader>
                <TableHeader>Race</TableHeader>
                <TableHeader>Total Time</TableHeader>
                <TableHeader>Overall Rank</TableHeader>
                <TableHeader>Age Group</TableHeader>
                <TableHeader>Status</TableHeader>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((raceResult) => (
                <TableRow key={raceResult.id} onClick={() => onView?.(raceResult)}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: theme.typography.sm }}>
                      <Calendar size={14} color="currentColor" />
                      {new Date(raceResult.date).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div style={{ fontWeight: '500' }}>{raceResult.name}</div>
                      <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '2px' }}>
                        <RaceType>
                          {getRaceTypeConfig(raceResult.raceType)?.label ||
                            raceResult.raceType.replace('_', ' ').toUpperCase()}
                        </RaceType>
                        {raceResult.location && (
                          <>
                            {' • '}
                            {raceResult.location}
                          </>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {raceResult.status === 'completed' && raceResult.totalTime ? formatTime(raceResult.totalTime) : '—'}
                  </TableCell>
                  <TableCell>
                    {raceResult.status === 'completed' && raceResult.overallRank && raceResult.totalParticipants ? (
                      <RankCell>
                        <RankValue $type="overall">
                          {raceResult.overallRank}/{raceResult.totalParticipants}
                        </RankValue>
                        <RankValue $type="overall">{raceResult.overallPercentile}th percentile</RankValue>
                      </RankCell>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {raceResult.status === 'completed' &&
                    raceResult.ageGroupRank &&
                    raceResult.ageGroupParticipants &&
                    raceResult.ageGroup ? (
                      <RankCell>
                        <RankValue $type="ageGroup">
                          {raceResult.ageGroupRank}/{raceResult.ageGroupParticipants} ({raceResult.ageGroup})
                        </RankValue>
                        <RankValue $type="ageGroup">{raceResult.ageGroupPercentile}th percentile</RankValue>
                      </RankCell>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge $status={raceResult.status}>{raceResult.status}</StatusBadge>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      {filteredData.length > 0 && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, filteredData.length)} of{' '}
            {filteredData.length} results
          </PaginationInfo>

          <PaginationControls>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Show:</span>
            <PageSelector value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </PageSelector>

            <PaginationButton
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <ChevronLeft size={16} />
            </PaginationButton>

            <span
              style={{
                fontSize: '0.875rem',
                color: 'var(--color-text-secondary)',
                minWidth: '60px',
                textAlign: 'center',
              }}
            >
              {totalPages > 0 ? `${currentPage} / ${totalPages}` : '0 / 0'}
            </span>

            <PaginationButton
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              title="Next page"
            >
              <ChevronRight size={16} />
            </PaginationButton>
          </PaginationControls>
        </PaginationContainer>
      )}

      {/* Race Result Modal */}
      <RaceResultModal
        athleteId={athleteId}
        isOpen={showRaceResultModal}
        onClose={handleCloseModal}
        onSave={handleSaveRaceResult}
        onDelete={handleDeleteRaceResult}
        raceResult={editingRaceResult}
      />
    </Container>
  )
}
